// Public CSAT rating page (/csat/:token) — NO login. Renders a satisfaction
// survey for an anonymous requester and posts the rating. The token holder can
// submit AND later update their feedback via the same link, so opening it always
// shows the form (prefilled with any prior rating/comment) rather than a
// dead-end confirmation. Lives OUTSIDE the authed Layout/RequireAuth in App.tsx
// (like PublicFormPage).

import { useEffect, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ApiError, getCsat, submitCsat } from "../lib/api";
import { Alert, Loading, Spinner } from "../components/States";
import { Logo } from "../components/Logo";
import type { CsatPublic } from "../lib/types";

const EMOJIS = ["😞", "😐", "🙂"];
const VALUES = [1, 3, 5];

export function CsatPage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const [survey, setSurvey] = useState<CsatPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false); // just submitted/updated this session
  const [updating, setUpdating] = useState(false); // editing after a prior response

  // Load the survey anonymously; token scopes it to one ticket.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getCsat(token)
      .then((res) => {
        if (cancelled) return;
        setSurvey(res);
        // Prefill: the email's emoji link (?r=) wins, else the requester's prior rating.
        const r = Number(params.get("r"));
        setRating(VALUES.includes(r) ? r : res.current_rating);
        setComment(res.current_comment ?? "");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof ApiError && e.status === 404
            ? "This survey isn't available. The link may be incorrect or has expired."
            : e instanceof Error
              ? e.message
              : "Could not load this survey.",
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (rating == null || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitCsat(token, { rating, comment: comment.trim() || null });
      setSaved(true);
      setUpdating(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const labels = survey?.labels?.length === 3 ? survey.labels : ["Bad", "OK", "Great"];
  const logoUrl = survey?.branding?.logo_url ?? null;
  // Show the confirmation after a submit (unless the user chose to edit again).
  const showConfirmation = saved && !updating;

  return (
    <div className="public-form-shell">
      <div className="public-form-card">
        <div className="public-form-brand">
          {logoUrl ? <img src={logoUrl} alt="" className="public-form-logo" /> : <Logo size={26} />}
        </div>

        {loading ? (
          <Loading label="Loading…" />
        ) : loadError ? (
          <Alert kind="error">{loadError}</Alert>
        ) : showConfirmation ? (
          <div className="public-form-done">
            <div className="public-form-check" aria-hidden="true">✓</div>
            <h1>{survey?.thankyou || "Thank you!"}</h1>
            <p className="muted">Your feedback on ticket {survey?.number} has been recorded.</p>
            <button
              type="button"
              className="btn"
              style={{ marginTop: 12 }}
              onClick={() => {
                setSaved(false);
                setUpdating(true);
              }}
            >
              Change my feedback
            </button>
          </div>
        ) : survey ? (
          <>
            <h1 className="public-form-title">{survey.question}</h1>
            <p className="public-form-desc">
              Ticket {survey.number} — {survey.subject}
            </p>
            {survey.already_responded && !saved ? (
              <Alert kind="info">You already rated this ticket — you can update your feedback below.</Alert>
            ) : null}
            <form onSubmit={onSubmit} className="form-preview">
              {submitError ? <Alert kind="error">{submitError}</Alert> : null}
              <div style={{ display: "flex", gap: 12, justifyContent: "center", margin: "14px 0" }}>
                {VALUES.map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRating(v)}
                    aria-pressed={rating === v}
                    className={`btn${rating === v ? " primary" : ""}`}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "12px 16px", minWidth: 84 }}
                  >
                    <span style={{ fontSize: 30 }} aria-hidden="true">{EMOJIS[i]}</span>
                    <span>{labels[i]}</span>
                  </button>
                ))}
              </div>
              <label className="field">
                <span className="label">{survey.comment_label || "Any comments? (optional)"}</span>
                <textarea
                  rows={3}
                  value={comment}
                  maxLength={2000}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={survey.comment_placeholder || "Tell us more…"}
                />
              </label>
              <button className="btn primary" type="submit" disabled={rating == null || submitting} style={{ marginTop: 8 }}>
                {submitting ? <Spinner label="Sending…" /> : survey.submit_label || "Submit feedback"}
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}
